import {useContext, useEffect, useRef, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {OrganizationRepository} from "../../repositories/OrganizationRepository";
import {ProjectRepository} from "../../repositories/ProjectRepository";
import {deleteDoc, doc} from "firebase/firestore";
import {db} from "../../config/firebase";
import {PROJECTS_COLLECTION} from "../../db/collections";
import {SignInContext} from "../../context/SignInContext";
import {Context as AppContext, ProjectRoles} from "../../context/AppContext";
import {ProjectEntity} from "../../model/ModelData";
import {v4 as uuid} from "uuid";
import "./onboarding.css";

const MAX_KB_CHARS = 5000;

const STEPS = [
    "Creating your account.",
    "Creating your organization.",
    "Preparing your project.",
    "Scanning your website.",
    "Generating your agent flow.",
    "Saving your generated flow.",
    "Opening your flow editor.",
];

const STATUS_MESSAGES = [
    "Securely preparing your workspace.",
    "Setting up your organization.",
    "Saving the first version of your project.",
    "Reading your website to learn about your business.",
    "Your prompt is being turned into a working flow. This can take a moment.",
    "Applying the generated flow to your project.",
    "Everything is ready. Opening the flow editor now.",
];
const FRIENDLY_ERROR_MESSAGES = {
    setup: "We couldn't complete the setup. Please try again.",
    organization: "We couldn't create your organization. Please try again.",
    project: "We couldn't create your project. Please try again.",
    generation: "We couldn't generate your AI agent. Please try again.",
    save: "We couldn't save your generated flow. Please try again.",
    publish: "We couldn't publish your flow. Please try again.",
};

const fetchSiteKnowledge = async (siteUrl, baseUrl) => {
    const url = siteUrl?.trim();
    if (!url) return "";
    try {
        const scrapeUrl = new URL("/api/scrape", baseUrl).toString();
        const response = await fetch(scrapeUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify({
                url,
                mode: "auto",
                use_cache: true,
            }),
        });
        if (!response.ok) return "";
        const data = await response.json();
        return (data?.markdown || "").trim().slice(0, MAX_KB_CHARS);
    } catch {
        return "";
    }
};
const collectFlowIds = (value, ids = []) => {
    if (!value || typeof value !== "object") return ids;

    if (Array.isArray(value)) {
        value.forEach((item) => collectFlowIds(item, ids));
        return ids;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
        if (key === "id" && typeof nestedValue === "string" && nestedValue) {
            ids.push(nestedValue);
        } else if (key !== "data") {
            collectFlowIds(nestedValue, ids);
        }
    });

    return ids;
};

const mergeCollection = (current = [], generated = []) => {
    const merged = [...(Array.isArray(current) ? current : [])];
    const indexes = new Map();

    merged.forEach((item, index) => {
        const key = item?.id || item?.name;
        if (key) indexes.set(key, index);
    });

    (Array.isArray(generated) ? generated : []).forEach((item) => {
        const key = item?.id || item?.name;
        if (!key) {
            merged.push(item);
            return;
        }

        if (indexes.has(key)) {
            merged[indexes.get(key)] = {...merged[indexes.get(key)], ...item};
        } else {
            indexes.set(key, merged.length);
            merged.push(item);
        }
    });

    return merged;
};

const mergeNamedCollection = (current = [], generated = []) => {
    const merged = [...(Array.isArray(current) ? current : [])];
    const indexes = new Map();

    merged.forEach((item, index) => {
        if (item?.name) indexes.set(item.name, index);
    });

    (Array.isArray(generated) ? generated : []).forEach((item) => {
        if (!item?.name) {
            merged.push(item);
            return;
        }

        if (indexes.has(item.name)) {
            const index = indexes.get(item.name);
            merged[index] = {...merged[index], ...item};
        } else {
            indexes.set(item.name, merged.length);
            merged.push(item);
        }
    });

    return merged;
};

const toCollection = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") return Object.values(value);
    return [];
};

const mergeRecord = (current = {}, generated = {}) => ({
    ...(current && typeof current === "object" && !Array.isArray(current) ? current : {}),
    ...(generated && typeof generated === "object" && !Array.isArray(generated) ? generated : {}),
});

const mergeGeneratedProject = (project, responseData) => {
    const generatedRoot = responseData?.project || responseData?.data?.project || responseData?.data || responseData;
    const generatedFlow = generatedRoot?.flow
        || responseData?.flow
        || (Array.isArray(responseData?.nodes) ? responseData : null);
    const generated = generatedRoot && typeof generatedRoot === "object" ? generatedRoot : {};
    const mergedProject = {...project};
    const resourceSources = generatedFlow && generatedFlow !== generated
        ? [generated, generatedFlow]
        : [generated];

    ["intents", "entities", "constants", "variables"].forEach((key) => {
        const generatedValues = resourceSources
            .map((source) => source?.[key])
            .filter((value) => value !== undefined);
        if (generatedValues.length === 0) return;

        if (key === "constants" || key === "variables") {
            mergedProject[key] = generatedValues.reduce((current, generatedValue) => (
                Array.isArray(generatedValue)
                    ? mergeCollection(current, generatedValue)
                    : mergeRecord(current, generatedValue)
            ), project[key]);
        } else {
            mergedProject[key] = generatedValues.reduce((current, generatedValue) => {
                const collection = toCollection(generatedValue).map((entity) => key === "entities"
                    ? (() => {
                        const entityType = entity?.entity_type || entity?.entityType || entity?.type || "FreeTextEntity";
                        return {
                            ...entity,
                            ...(entityType ? {
                                entity_type: entityType,
                                // Compatibility with the currently deployed Firebase mapper.
                                entityType: entityType,
                            } : {}),
                        };
                    })()
                    : entity);
                return mergeNamedCollection(current, collection);
            }, project[key]);
        }
    });

    if (generatedFlow) {
        const currentFlow = project.flows?.[0];
        const normalizedFlow = {
            ...generatedFlow,
            id: generatedFlow.id || currentFlow?.id || uuid(),
            entryNode: generatedFlow.entryNode || generatedFlow["entry-node"] || currentFlow?.entryNode,
        };

        delete normalizedFlow["entry-node"];
        mergedProject.flows = mergeCollection(project.flows, [normalizedFlow]);

        if (normalizedFlow.entryNode) {
            mergedProject.entryNode = normalizedFlow.entryNode;
        }
    }

    return mergedProject;
};

export const OnboardingPage = () => {

    const {currentUser} = useContext(SignInContext);
    const {createProject, updateProject, publishVersion} = useContext(AppContext);
    const navigate = useNavigate();
    const started = useRef(false);
    const [step, setStep] = useState(0);
    const [error, setError] = useState("");
    const baseUrl = process.env.REACT_APP_AI_FLOW_BUILDER;

    useEffect(() => {
        if (!currentUser || started.current) return;

        const hasPrompt = sessionStorage.getItem("openbrainAgentPrompt");
        const hasLanguage = sessionStorage.getItem("openbrainAgentLanguage");
        if (!hasPrompt || !hasLanguage) {
            navigate("/projects", {replace: true});
            return;
        }

        started.current = true;

        const runOnboarding = async () => {
            let failedOperation = "setup";
            // set once the backend project / its Firebase flow exist, so a failed creation can be rolled back
            let createdProjectId = null;
            let createdFlowUid = null;
            try {
                const prompt = sessionStorage.getItem("openbrainAgentPrompt") || "";
                const storedLanguage = sessionStorage.getItem("openbrainAgentLanguage");
                const siteUrl = sessionStorage.getItem("site_url" || "");

                let language;

                try {
                    language = storedLanguage ? JSON.parse(storedLanguage) : null;
                } catch {
                    throw new Error("Selected language data is invalid.");
                }

                if (!language?.id || !language?.code || !language?.lang) {
                    throw new Error("No valid language was selected.");
                }
                const redirect_message = sessionStorage.getItem("redirect_message");
                const ownerName = [currentUser.name, currentUser.lastname || currentUser.lastName]
                    .filter(Boolean)
                    .join(" ") || "My";
                failedOperation = "organization";

                setStep(1);
                const organizationResponse = await OrganizationRepository.create({
                    name: `${ownerName}'s Organization`,
                    organization_type: "CUSTOMER",
                    members: [currentUser.username],
                    published: false,
                });

                const organizationId = organizationResponse?.data?.id;
                if (!organizationId) throw new Error("Organization could not be created");

                setStep(2);
                // the backend creates the project and one sub project; the sub project's flowUid is the Firebase flow id
                failedOperation = "project";
                const projectName = `${currentUser.name}'s AI agent`;
                const projectResponse = await ProjectRepository.create({
                    name: projectName,
                    description: prompt,
                    organizationId,
                    backendConfigurationId: null,
                    // no name: the backend defaults it to the language's name
                    subProjects: [{name: null, description: null, languageId: language.id}],
                    type: "VOICE_ASSISTANT",
                });
                createdProjectId = projectResponse?.data?.id;
                const subProject = projectResponse?.data?.subProjects?.[0];
                const projectId = subProject?.flowUid;
                if (!createdProjectId || !projectId) throw new Error("The project was created without a sub project");
                const configuration = {
                    "mcp_registry": "https://client.mcp.zmc.openbrain.io/",
                    "max_calls_by_user": null,
                    "conversation_logger_api": "https://api.vb.zmc.openbrain.io",
                    "is_live": true,
                    "chatbot_api": "https://stage.core.zmc.openbrain.io/api/receive"
                };
                const project = {
                    ...ProjectEntity,
                    id: projectId,
                    name: projectName,
                    is_demo: true,
                    redirect_message: redirect_message,
                    description: prompt,
                    configuration: configuration,
                    team: [currentUser.username],
                    org_id: organizationId,
                    members: [{username: currentUser.username, role: ProjectRoles.ADMIN, vdn: ""}],
                    settings: {
                        ...ProjectEntity.settings,
                        speechToText: 51,
                        status: "BUILDING_SCENARIOS",
                        mode: "stage",
                        organization: organizationId,
                        language: language.id,
                        lang: language.lang,
                        org_id: organizationId,
                        projectType: "VOICE_BOT",
                    },
                };
                failedOperation = "project";
                setStep(2);
                await createProject(project);
                createdFlowUid = projectId;
                setStep(3);
                const knowledgeBase = await fetchSiteKnowledge(siteUrl, baseUrl);

                failedOperation = "generation";
                setStep(4);
                const augmentedMessage = knowledgeBase
                    ? `${prompt}

## About the Business
You are building this flow for the business described below. Use this information to design a flow that fits how this business actually operates.

<business_knowledge>
${knowledgeBase}
</business_knowledge>

When designing the flow:
- Base the flow's paths on the business's real services, products, and processes (e.g. if they offer booking, include a booking path; if they list FAQs, cover those topics).
- Write node messages in a way that reflects this business, using its real names, offerings, and details instead of generic placeholders.
- Match the tone to the business (e.g. formal for a clinic, casual for a café).
- Only use facts that appear above.`
                    : prompt;

                const flowGenerationPayload = {
                    message: augmentedMessage,
                    language: language.lang,
                    known_intent_ids: (project.intents || [])
                        .map((intent) => intent?.id)
                        .filter(Boolean),
                    existing_ids: [...new Set(collectFlowIds(project.flows || []))],
                };
                const createFlowUrl = new URL("/api/create-flow", baseUrl).toString();
                const response = await fetch(createFlowUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`,
                    },
                    body: JSON.stringify(flowGenerationPayload),
                });

                const responseText = await response.text();

                let responseData;
                try {
                    responseData = JSON.parse(responseText);
                } catch {
                    throw new Error("Flow generation returned an invalid response.");
                }

                if (!response.ok) {
                    throw new Error(
                        responseData?.detail ||
                        responseData?.message ||
                        `Flow generation failed with status ${response.status}.`
                    );
                }

                if (!responseData || typeof responseData !== "object") {
                    throw new Error("Flow generation returned empty or invalid data.");
                }

                const mergedProject = mergeGeneratedProject(project, responseData);
                failedOperation = "save";
                setStep(5);
                await updateProject(mergedProject);
                failedOperation = "publish";
                setStep(6);
                await publishVersion(mergedProject);

                sessionStorage.removeItem("openbrainAgentPrompt");
                sessionStorage.removeItem("openbrainAgentLanguage");
                sessionStorage.removeItem("redirect_message");
                sessionStorage.removeItem("site_url");

                setStep(STEPS.length - 1);
                setTimeout(() => navigate(`/project/${projectId}/flow`, {replace: true}), 700);
            } catch (onboardingError) {
                console.error("Onboarding failed:", {
                    operation: failedOperation,
                    error: onboardingError,
                });

                // roll back so the backend project and its Firebase flow are created together or not at all;
                // later failures (generation, save, publish) keep the created project
                if (failedOperation === "project") {
                    if (createdFlowUid) {
                        await deleteDoc(doc(db, PROJECTS_COLLECTION, createdFlowUid)).catch(e => console.error(e));
                    }
                    if (createdProjectId) {
                        await ProjectRepository.delete(createdProjectId).catch(e => console.error(e));
                    }
                }

                setError(
                    FRIENDLY_ERROR_MESSAGES[failedOperation] ||
                    FRIENDLY_ERROR_MESSAGES.setup
                );
            }
        };
        runOnboarding();
    }, [currentUser, createProject, updateProject, navigate, publishVersion, baseUrl]);


    const total = STEPS.length;
    const isComplete = step >= total - 1;
    const currentNumber = Math.min(step + 1, total);
    const progress = Math.round((step / (total - 1)) * 100);

    return (
        <Box className="onboarding-page" sx={(theme) => ({
            "--onboarding-primary": theme.palette.primary.main,
            "--onboarding-primary-dark": theme.palette.primary.dark || theme.palette.primary.main,
            "--onboarding-background": theme.palette.background1,
            "--onboarding-surface": theme.palette.white,
            "--onboarding-text": theme.palette.text.primary,
            "--onboarding-muted": theme.palette.text.secondary,
            "--onboarding-success": theme.palette.success.main,
            "--onboarding-error": theme.palette.error.main,
        })}>
            <Box className="onboarding-card">
                <Box className="onboarding-orb" aria-hidden="true"/>
                <Typography className="onboarding-eyebrow">OPENBRAIN SETUP</Typography>
                <Typography component="h1" className="onboarding-title">Your workspace is<br/>coming
                    together.</Typography>
                <Typography className="onboarding-description">We’re preparing your organization and agent flow. This
                    will only take a moment.</Typography>

                <Box className="onboarding-progress-head">
                    <span className="onboarding-progress-count">Step {currentNumber} of {total}</span>
                    <span className="onboarding-progress-pct">{progress}%</span>
                </Box>
                <Box
                    className="onboarding-progress"
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Onboarding progress"
                >
                    <Box className={`onboarding-progress-fill ${isComplete ? "is-complete" : ""}`}
                         style={{width: `${progress}%`}}/>
                </Box>

                <Box className="onboarding-steps" role="list">
                    {STEPS.map((label, index) => {
                        const state = index < step ? "completed" : index === step ? "active" : "upcoming";
                        return (
                            <Box key={label} className={`onboarding-step ${state}`} role="listitem">
                                <Box className="onboarding-step-indicator" aria-hidden="true">
                                    {state === "completed" ? (
                                        <span className="onboarding-check">✓</span>
                                    ) : state === "active" ? (
                                        <CircularProgress size={16} thickness={5} className="onboarding-spinner"/>
                                    ) : (
                                        index + 1
                                    )}
                                </Box>
                                <Box className="onboarding-step-text">
                                    <Typography className="onboarding-step-label">{label}</Typography>
                                    {state === "active" && (
                                        <Typography className="onboarding-step-hint">
                                            {STATUS_MESSAGES[Math.min(step, STATUS_MESSAGES.length - 1)]}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {error && <Typography className="onboarding-error">{error}</Typography>}
            </Box>
        </Box>
    );
};

export default OnboardingPage;
